// src/pages/Leads.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getLeads, getForms, getServices, downloadCSV } from '../utils/api';
import { StatusBadge, SourceBadge, EmptyState, formatDate } from '../components/UI';
import LeadForm from '../components/LeadForm';
import { useAuth } from '../context/AuthContext';
import { useLeadLookups } from '../hooks/useLeadLookups';

const DATE_PRESETS = [
  { value: 'all',    label: 'All Time' },
  { value: 'today',  label: 'Today' },
  { value: 'week',   label: 'This Week' },
  { value: 'month',  label: 'This Month' },
  { value: 'last30', label: 'Last 30 Days' },
  { value: 'fy',     label: 'This FY' },
  { value: 'custom', label: 'Custom Range' },
];

function getDateRange(preset, customFrom, customTo) {
  const today = new Date().toISOString().split('T')[0];
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'week') {
    const d = new Date();
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    return { from: d.toISOString().split('T')[0], to: today };
  }
  if (preset === 'month') {
    const d = new Date();
    return { from: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`, to: today };
  }
  if (preset === 'last30') {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return { from: d.toISOString().split('T')[0], to: today };
  }
  if (preset === 'fy') {
    const d = new Date();
    const fyYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    return { from: `${fyYear}-04-01`, to: today };
  }
  if (preset === 'custom') return { from: customFrom, to: customTo };
  return {};
}

const selectStyle = { height: 34, padding: '0 10px', fontSize: 13, width: 'auto', minWidth: 130, borderRadius: 7, border: '0.5px solid var(--gray-200)', background: 'var(--white)', fontFamily: 'var(--font)', color: 'var(--black)', cursor: 'pointer', outline: 'none' };
const dateInputStyle = { height: 34, padding: '0 8px', fontSize: 12, border: '0.5px solid var(--gray-200)', borderRadius: 7, fontFamily: 'var(--font)', color: 'var(--black)', background: 'var(--white)', outline: 'none' };

export default function Leads({ toast }) {
  const location = useLocation();
  const navState = location.state || {};
  const { user: me, can } = useAuth();

  const [leads,          setLeads]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [filter,         setFilter]         = useState(navState.status     || 'all');
  const [sourceFilter,   setSourceFilter]   = useState('');
  const [serviceFilter,  setServiceFilter]  = useState(navState.service || '');
  const [formFilter,     setFormFilter]     = useState('');
  const [datePreset,     setDatePreset]     = useState(navState.datePreset || 'all');
  const [dateFrom,       setDateFrom]       = useState('');
  const [dateTo,         setDateTo]         = useState('');
  const [q,              setQ]              = useState('');
  const [showForm,       setShowForm]       = useState(false);
  const [editLead,       setEditLead]       = useState(null);
  const [forms,          setForms]          = useState([]);
  const [services,       setServices]       = useState([]);
  const { statuses, sources } = useLeadLookups();
  const navigate = useNavigate();

  // Clear navigation state so back button doesn't re-apply filters
  useEffect(() => {
    if (location.state) window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    getForms().then(r => setForms(r.data)).catch(() => {});
    getServices().then(r => setServices(r.data.map(s => s.name))).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filter !== 'all')  params.status  = filter;
    if (sourceFilter)      params.source  = sourceFilter;
    if (serviceFilter)     params.service = serviceFilter;
    if (formFilter)        params.form_id = formFilter;
    if (q)                 params.q       = q;
    const { from, to } = getDateRange(datePreset, dateFrom, dateTo);
    if (from) params.from = from;
    if (to)   params.to   = to;
    getLeads(params)
      .then(r => setLeads(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter, sourceFilter, serviceFilter, formFilter, q, datePreset, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    window.addEventListener('leads-refresh', load);
    return () => window.removeEventListener('leads-refresh', load);
  }, [load]);

  const onSaved = (saved) => {
    setLeads(prev => {
      const idx = prev.findIndex(l => l.id === saved.id);
      return idx >= 0 ? prev.map(l => l.id === saved.id ? saved : l) : [saved, ...prev];
    });
  };

  const clearFilters = () => {
    setFilter('all');
    setSourceFilter('');
    setServiceFilter('');
    setFormFilter('');
    setDatePreset('all');
    setDateFrom('');
    setDateTo('');
    setQ('');
  };

  const hasActiveFilters = filter !== 'all' || sourceFilter || serviceFilter || formFilter || datePreset !== 'all' || q;

  return (
    <div>
      <div className="section-header">
        <div className="section-title">All Leads</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {can('leads.export') && (
            <button className="btn btn-outline btn-sm" onClick={() => downloadCSV().catch(() => alert('Export failed'))}>
              <i className="ti ti-download" /> Export CSV
            </button>
          )}
          {can('leads.create') && (
            <button className="btn btn-primary btn-sm" onClick={() => { setEditLead(null); setShowForm(true); }}>
              <i className="ti ti-plus" /> Add Lead
            </button>
          )}
        </div>
      </div>

      {/* Status filter pills */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <div className="filters" style={{ margin: 0 }}>
          <button className={`filter-btn${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>All</button>
          {statuses.map(s => (
            <button key={s.id} className={`filter-btn${filter === s.name ? ' active' : ''}`} onClick={() => setFilter(s.name)}>
              {s.name}
            </button>
          ))}
        </div>
        {hasActiveFilters && (
          <button onClick={clearFilters} style={{ fontSize: 12, color: 'var(--gray-400)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px' }}>
            <i className="ti ti-x" style={{ fontSize: 11 }} /> Clear
          </button>
        )}
      </div>

      {/* Secondary filters row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>

        <select style={selectStyle} value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
          <option value="">All Sources</option>
          {sources.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
        </select>

        {services.length > 0 && (
          <select style={selectStyle} value={serviceFilter} onChange={e => setServiceFilter(e.target.value)}>
            <option value="">All Services</option>
            {services.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        {forms.length > 0 && (
          <select style={selectStyle} value={formFilter} onChange={e => setFormFilter(e.target.value)}>
            <option value="">All Forms</option>
            {forms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        )}

        <select style={selectStyle} value={datePreset} onChange={e => setDatePreset(e.target.value)}>
          {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>

        {datePreset === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="date" style={dateInputStyle} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>to</span>
            <input type="date" style={dateInputStyle} value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
        )}

        <div className="search-wrap" style={{ marginLeft: 'auto' }}>
          <i className="ti ti-search" />
          <input className="search-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, phone, email, city..." />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</div>
        ) : leads.length === 0 ? (
          <EmptyState icon="ti-users" text="No leads found" />
        ) : (
          <table className="leads-table">
            <thead>
              <tr>
                <th>Name / Company</th>
                <th>Service</th>
                <th>Source</th>
                <th>Form</th>
                <th>Status</th>
                <th>Follow-up</th>
                <th>Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leads.map(l => (
                <tr key={l.id} onClick={() => navigate(`/leads/${l.id}`)}>
                  <td>
                    <div className="lead-name">{l.name}</div>
                    <div className="lead-company">{l.company}</div>
                  </td>
                  <td>{l.service || '—'}</td>
                  <td><SourceBadge source={l.source} sources={sources} /></td>
                  <td style={{ fontSize: 12, color: l.form_name ? 'var(--gray-600)' : 'var(--gray-300)' }}>
                    {l.form_name || '—'}
                  </td>
                  <td><StatusBadge status={l.status} statuses={statuses} /></td>
                  <td style={{ color: l.followup_date && l.followup_date <= new Date().toISOString().split('T')[0] ? 'var(--red)' : 'var(--gray-600)' }}>
                    {formatDate(l.followup_date)}
                  </td>
                  <td style={{ color: 'var(--gray-400)' }}>{formatDate(l.added_date)}</td>
                  <td onClick={e => e.stopPropagation()}>
                    {can('leads.edit') && (
                      <button className="btn btn-outline btn-sm" onClick={() => { setEditLead(l); setShowForm(true); }}>
                        <i className="ti ti-edit" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <LeadForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditLead(null); }}
        lead={editLead}
        onSaved={onSaved}
        toast={toast}
      />
    </div>
  );
}
